import { useCallback, useEffect, useMemo, useState } from 'react';
import { SigmaContainer, useLoadGraph, useSigma } from '@react-sigma/core';
import '@react-sigma/core/lib/style.css';
import { MultiDirectedGraph } from 'graphology';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import { clsx } from 'clsx';
import { Loader2, MessageSquare, RefreshCw, Search, X } from 'lucide-react';
import {
  explorerApi,
  type ExplorerAnswer,
  type ExplorerGraph,
  type ExplorerNode,
  type ExplorerNodeDetail,
  type ExplorerStatus,
} from '@/services/api';

/** Mining's own entities (Layer A) first, then what EdgeQuake extracted from the text. */
const TYPE_COLORS: Record<string, string> = {
  BUYER_CONCEPT: '#2dd4bf',
  STRATEGY: '#a78bfa',
  TEMPLATE: '#f472b6',
  BRAND: '#60a5fa',
  ADVERTISER: '#60a5fa', // before the 2026-10-17 schema: now BRAND
  PRODUCT: '#fb923c',
  SHELF: '#94a3b8',
  CLIENT: '#facc15',
  CATEGORY: '#e2e8f0',
  BUYER_CONCERN: '#f87171',
  OBJECTION: '#ef4444',
  BENEFIT: '#34d399',
  PRODUCT_FEATURE: '#fdba74',
  CLAIM: '#c4b5fd',
  PROOF: '#86efac',
  OFFER: '#fde047',
  USE_CASE: '#67e8f9',
  VISUAL_ELEMENT: '#f9a8d4',
  SETTING: '#93c5fd',
  PERSONA_TRAIT: '#fcd34d',
  EMOTION: '#fda4af',
  CERTIFICATION: '#4ade80',
  WARRANTY_TERM: '#a3e635',
  PRICE_POINT: '#fbbf24',
};
const colorOf = (t: string) => TYPE_COLORS[t] ?? '#64748b';

const SIGMA_SETTINGS = {
  allowInvalidContainer: true,
  renderLabels: true,
  labelFont: 'Inter, system-ui, sans-serif',
  labelSize: 11,
  labelColor: { color: '#e2e8f0' },
  labelDensity: 0.6,
  labelRenderedSizeThreshold: 6,
  stagePadding: 30,
  defaultEdgeType: 'line',
  zIndex: true,
} as const;

function GraphLoader({
  data,
  hidden,
  onNodeClick,
}: {
  data: ExplorerGraph;
  hidden: Set<string>;
  onNodeClick: (n: ExplorerNode) => void;
}) {
  const loadGraph = useLoadGraph();
  const sigma = useSigma();
  const byId = useMemo(() => new Map(data.nodes.map((n) => [n.id, n])), [data]);

  useEffect(() => {
    const g = new MultiDirectedGraph();
    const maxDeg = Math.max(1, ...data.nodes.map((n) => n.degree ?? 1));
    for (const n of data.nodes) {
      if (hidden.has(n.type) || g.hasNode(n.id)) continue;
      g.addNode(n.id, {
        label: n.label,
        size: 3 + 11 * Math.sqrt((n.degree ?? 1) / maxDeg),
        color: colorOf(n.type),
        x: Math.random(),
        y: Math.random(),
      });
    }
    for (const e of data.edges) {
      if (!g.hasNode(e.source) || !g.hasNode(e.target)) continue;
      try {
        g.addDirectedEdgeWithKey(e.id, e.source, e.target, { size: 1, color: '#334155', label: e.type });
      } catch {
        // duplicate edge id
      }
    }
    if (g.order > 1) forceAtlas2.assign(g, { iterations: 200, settings: { gravity: 1, scalingRatio: 8, slowDown: 8 } });
    loadGraph(g);
  }, [data, hidden, loadGraph]);

  useEffect(() => {
    const handler = ({ node }: { node: string }) => {
      const n = byId.get(node);
      if (n) onNodeClick(n);
    };
    sigma.on('clickNode', handler);
    return () => {
      sigma.off('clickNode', handler);
    };
  }, [sigma, byId, onNodeClick]);

  return null;
}

export function DocketExploreTab({
  docketId,
  onOpenItem,
}: {
  docketId: string;
  onOpenItem?: (itemId: string) => void;
}) {
  const [status, setStatus] = useState<ExplorerStatus | null>(null);
  const [graph, setGraph] = useState<ExplorerGraph | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set(['PRODUCT']));
  const [selected, setSelected] = useState<ExplorerNodeDetail | null>(null);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<ExplorerNode[] | null>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<ExplorerAnswer | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(() => {
    explorerApi
      .status(docketId)
      .then(setStatus)
      .catch((e: Error) => setError(e.message));
  }, [docketId]);
  useEffect(loadStatus, [loadStatus]);

  useEffect(() => {
    if (!status?.synced) return;
    setBusy('graph');
    explorerApi
      .graph(docketId, { focus, depth: focus ? 2 : 2, maxNodes: focus ? 200 : 400 })
      .then(setGraph)
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(null));
  }, [docketId, status?.synced, focus]);

  const openNode = useCallback(
    (n: ExplorerNode | { label: string }) => {
      explorerApi
        .node(docketId, n.label)
        .then(setSelected)
        .catch((e: Error) => setError(e.message));
    },
    [docketId],
  );

  const types = useMemo(() => {
    const counts = new Map<string, number>();
    for (const n of graph?.nodes ?? []) counts.set(n.type, (counts.get(n.type) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [graph]);

  const sync = () => {
    setBusy('sync');
    explorerApi
      .sync(docketId)
      .then(loadStatus)
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(null));
  };

  if (status && !status.configured) {
    return (
      <p className="text-sm text-slate-500">
        No Knowledge Explorer is configured for this environment (KNOWLEDGE_EXPLORER_URL).
      </p>
    );
  }
  if (status && !status.synced) {
    return (
      <div className="text-sm text-slate-600 space-y-2">
        <p>This library has not been loaded into the Knowledge Explorer yet.</p>
        <button
          type="button"
          onClick={sync}
          disabled={busy === 'sync'}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50"
        >
          {busy === 'sync' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Sync structure (free; text extraction is run separately)
        </button>
      </div>
    );
  }

  const s = status?.stats;
  const ng = selected?.metadata?.ng_id as string | undefined;

  return (
    <div className="space-y-3">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex justify-between">
          {error}
          <button type="button" onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        {s && (
          <span>
            {s.entity_count?.toLocaleString()} entities · {s.relationship_count?.toLocaleString()} relationships ·{' '}
            {s.document_count?.toLocaleString()} documents
          </span>
        )}
        {graph?.truncated && (
          <span className="text-amber-700">
            showing {graph.nodes.length} of {graph.total_nodes?.toLocaleString()}; search or click to focus
          </span>
        )}
        {focus && (
          <button type="button" onClick={() => setFocus(null)} className="text-brand-teal hover:underline">
            ← whole library
          </button>
        )}
        <form
          className="ml-auto flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (query.trim().length < 2) return;
            explorerApi
              .search(docketId, query.trim())
              .then((r) => setHits(r.nodes))
              .catch((err: Error) => setError(err.message));
          }}
        >
          <Search size={12} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a concept, strategy, advertiser…"
            className="w-64 rounded border border-slate-200 px-2 py-1 text-xs"
          />
        </form>
      </div>

      {hits && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          {hits.length === 0 && <span className="text-slate-400">No matches</span>}
          {hits.slice(0, 15).map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => {
                setFocus(h.label);
                openNode(h);
                setHits(null);
              }}
              className="rounded-full border border-slate-200 px-2 py-0.5 hover:bg-slate-50"
            >
              <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: colorOf(h.type) }} />
              {h.label}
            </button>
          ))}
          <button type="button" onClick={() => setHits(null)} className="text-slate-400">
            <X size={12} />
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {types.map(([t, n]) => (
          <button
            key={t}
            type="button"
            onClick={() =>
              setHidden((h) => {
                const next = new Set(h);
                if (next.has(t)) next.delete(t);
                else next.add(t);
                return next;
              })
            }
            className={clsx(
              'rounded-full border px-2 py-0.5',
              hidden.has(t) ? 'border-slate-200 text-slate-400 line-through' : 'border-slate-300 text-slate-700',
            )}
          >
            <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: colorOf(t) }} />
            {t.toLowerCase().replace(/_/g, ' ')} {n}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-3">
        <div className="relative h-[560px] rounded-xl overflow-hidden" style={{ background: '#0f172a' }}>
          {busy === 'graph' && (
            <div className="absolute inset-0 z-10 flex items-center justify-center text-slate-300 text-sm">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading graph…
            </div>
          )}
          {graph && (
            <SigmaContainer
              graph={MultiDirectedGraph}
              settings={SIGMA_SETTINGS}
              style={{ position: 'absolute', inset: 0, background: '#0f172a' }}
            >
              <GraphLoader data={graph} hidden={hidden} onNodeClick={openNode} />
            </SigmaContainer>
          )}
        </div>

        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 p-3 text-sm min-h-[160px]">
            {!selected ? (
              <p className="text-xs text-slate-400">Click a node to see what it is and what it connects to.</p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="mt-1 inline-block w-2.5 h-2.5 rounded-full" style={{ background: colorOf(selected.type) }} />
                  <div>
                    <div className="font-medium text-navy-900">{selected.label}</div>
                    <div className="text-[11px] text-slate-500">
                      {selected.type?.toLowerCase().replace(/_/g, ' ')} · {selected.degree} links
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-600">{selected.description}</p>
                <div className="flex gap-3 text-xs">
                  <button type="button" onClick={() => setFocus(selected.label)} className="text-brand-teal hover:underline">
                    Focus here
                  </button>
                  {ng && onOpenItem && (
                    <button type="button" onClick={() => onOpenItem(ng)} className="text-brand-teal hover:underline">
                      Open library item {ng}
                    </button>
                  )}
                </div>
                <div className="max-h-48 overflow-auto text-xs space-y-0.5">
                  {[...selected.outgoing.map((r) => ({ ...r, dir: '→' })), ...selected.incoming.map((r) => ({ ...r, dir: '←' }))]
                    .slice(0, 60)
                    .map((r, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => openNode({ label: r.label })}
                        className="block text-left w-full hover:bg-slate-50 rounded px-1"
                      >
                        <span className="text-slate-400">
                          {r.dir} {r.type.toLowerCase().replace(/_/g, ' ')}
                        </span>{' '}
                        {r.label}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>

          <form
            className="rounded-xl border border-slate-200 p-3 space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!question.trim()) return;
              setBusy('ask');
              setAnswer(null);
              explorerApi
                .ask(docketId, question.trim())
                .then(setAnswer)
                .catch((err: Error) => setError(err.message))
                .finally(() => setBusy(null));
            }}
          >
            <label className="text-xs font-medium text-navy-900 flex items-center gap-1">
              <MessageSquare size={12} /> Ask this library
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={2}
              placeholder="What do buyers worry about, and which ads answer it?"
              className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
            />
            <button
              type="submit"
              disabled={busy === 'ask'}
              className="inline-flex items-center gap-1 rounded-lg bg-navy-900 text-white px-3 py-1 text-xs disabled:opacity-50"
            >
              {busy === 'ask' && <Loader2 size={12} className="animate-spin" />} Ask
            </button>
            {answer && (
              <div className="text-xs space-y-2">
                <div className="whitespace-pre-wrap text-slate-700 max-h-72 overflow-auto">
                  {answer.answer.replace(/\[([^\]]+)\]\([^)]*\)/g, '[$1]')}
                </div>
                <details>
                  <summary className="cursor-pointer text-slate-500">{answer.sources.length} sources</summary>
                  <ul className="mt-1 space-y-1">
                    {answer.sources.map((src, i) => (
                      <li key={i} className="text-slate-500">
                        <span className="font-mono text-[10px]">{src.document}</span>
                        <div className="text-slate-600">{src.snippet.slice(0, 200)}</div>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
